import React, { useContext, useEffect, useRef, useState } from "react";
import TokenClient, { OrganizationInfo, TokenResponse } from "../apiClients/TokenClient";
import { ClaimTypes, Securables, SessionStorageItemIds } from "../Enums";
import { IsEmpty, IsNotEmpty, IsNullOrUndefined } from "../Helpers";
import jwt_decode from "jwt-decode";
import fetchIntercept from 'fetch-intercept'
import Enumerable from "linq";
import dayjs, { Dayjs } from "dayjs";
import OrganizationSettingsClient, { OrganizationSettings } from "../apiClients/OrganizationSettingsClient";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { b2cPolicies, loginRequest, onboardingRequest, primarySignInPolicy, signInRequest } from "../AuthConfig";
import { InteractionStatus } from "@azure/msal-browser";
import { msalInstance } from "..";
import ManageOrganizationClient from "../apiClients/ManageOrganizationClient";
import { hasSubscriptionAccess, requiresSubscription } from "../subscriptionAccess";
import { AuthenticationTransitionBoundary } from "../components/AuthenticationTransition";
import { IDENTITY_FIRST_ONBOARDING } from "../Environment";
import { selectActiveAccount } from "../auth/selectActiveAccount";
import { ApiRequestError } from "../apiClients/base/ApiBase";
import EmployeePortalClient, { EmployeeInvitationConfirmation } from "../apiClients/EmployeePortalClient";
import {
    hasEmployeePortalAccess,
    hasProducerPortalAccess,
    PortalAccessDiscovery,
    PortalContextKind,
    PortalContextSelection,
    serializePortalContext,
    ProducerPortalContext,
} from "../models/portal-context";
import { choosePortalContext } from "../auth/portalRouting";

export interface AccessInfoProps {
    currentUserId: string;
    selectedOrganizationId: string;
    selectedProducerCompanyId?: string;
    selectedOrganizationName: string;
    selectedProducerCompanyName?: string;
    selectedOrganizationSettings: OrganizationSettings;
    roles: Enumerable.IEnumerable<string>;
    organizations: OrganizationInfo[],
    portalAccess: PortalAccessDiscovery;
    currentPortalContext: PortalContextSelection | null;
    given_name: string,
    family_name: string,
    email: string,
    impersonatorId?: string,
    impersonatorName?: string
}

export interface AccessTokenInfo {
    token: string;
    tokenExpiration: Dayjs;
}

export interface AccessInfoContextProps {
    accessInfo: AccessInfoProps;
    hasSubscriptionAccess: boolean;
    subscriptionRequired: boolean;
    hasRole: (role: Securables, preventOverrides?: boolean) => boolean;
    hasOneOfRoles: (roles: Securables[], preventOverrides?: boolean) => boolean;
    reloadAccessToken: () => Promise<any>;
    changeOrganization: (orgId: string) => Promise<any>;
    changePortalContext: (context: PortalContextSelection) => Promise<any>;
    reloadOrgSettings: () => Promise<any>;
    login: () => void;
    startOnboarding: () => void;
    startSetupInvitation: (invitationId: string) => void;
    startEmployeeInvitation: (invitationToken: string) => void;
    prepareEmployeeInvitationSession: (invitationToken: string, signal?: AbortSignal) => Promise<EmployeeInvitationConfirmation>;
    confirmEmployeeInvitation: (signal?: AbortSignal) => Promise<void>;
    switchEmployeeInvitationIdentity: (invitationToken: string) => void;
    logout: () => void;
    impersonate: (userId: string, organizationId: string, withAdmin: boolean) => Promise<void>;
    clearImpersonation: () => Promise<void>;
    isLoggedIn: () => boolean;
    isImpersonating: () => boolean;
    needsOnboarding: boolean;
    onboardingBootstrapError: string;
    reloadAfterOnboardingProvisioned: (organizationId: string) => Promise<boolean>;
    reloadAfterInvitationAccepted: (organizationId: string) => Promise<boolean>;
    reloadAfterEmployeeInvitationClaimed: () => Promise<boolean>;
    startProducerInvitation: (invitationId: string, invitationToken: string) => void;
    getFreshIdentityToken: () => Promise<string>;
    reloadAfterProducerInvitationClaimed: (organizationId?: string) => Promise<boolean>;
}

export const AccessInfoContext = React.createContext<AccessInfoContextProps>({
    accessInfo: null,
    hasSubscriptionAccess: false,
    subscriptionRequired: false,
    hasRole: () => false,
    hasOneOfRoles: (roles) => false,
    reloadAccessToken: () => Promise.resolve(),
    changeOrganization: x => Promise.resolve(),
    changePortalContext: x => Promise.resolve(),
    reloadOrgSettings: () => Promise.resolve(),
    login: () => { },
    startOnboarding: () => { },
    startSetupInvitation: () => { },
    startEmployeeInvitation: () => { },
    prepareEmployeeInvitationSession: () => Promise.reject(new Error('Invitation session is unavailable.')),
    confirmEmployeeInvitation: () => Promise.reject(new Error('Invitation session is unavailable.')),
    switchEmployeeInvitationIdentity: () => { },
    logout: () => { },
    impersonate: (userId, organizationId, withAdmin) => Promise.resolve(),
    clearImpersonation: () => Promise.resolve(),
    isLoggedIn: () => false,
    isImpersonating: () => false,
    needsOnboarding: false,
    onboardingBootstrapError: '',
    reloadAfterOnboardingProvisioned: () => Promise.resolve(false),
    reloadAfterInvitationAccepted: () => Promise.resolve(false),
    reloadAfterEmployeeInvitationClaimed: () => Promise.resolve(false),
    startProducerInvitation: () => { },
    getFreshIdentityToken: () => Promise.reject(new Error('Identity token is unavailable.')),
    reloadAfterProducerInvitationClaimed: () => Promise.resolve(false),
});

interface BusyContext {
    busy: boolean;
    msg: string;
}

const workspaceBootstrapRetryDelaysMs = [1_000, 2_000, 4_000, 8_000, 12_000, 15_000, 18_000];
const databaseTemporarilyUnavailableCode = 'DATABASE_TEMPORARILY_UNAVAILABLE';

export const isRetryableWorkspaceBootstrapError = (error: unknown): boolean => {
    return error instanceof ApiRequestError
        && error.status === 503
        && error.code === databaseTemporarilyUnavailableCode;
};

const normalizedRoles = (jwt: any): Securables[] => {
    const roleClaim = jwt?.[ClaimTypes.Roles];
    return (!roleClaim ? [] : Array.isArray(roleClaim) ? roleClaim : [roleClaim]) as Securables[];
};

const getCurrentProducerContextId = (jwt: any): string | undefined =>
    jwt?.[ClaimTypes.CurrentProducerContextId] ?? jwt?.CurrentProducerCompanyId;

export const validatePortalToken = (token: string, expectedContext: PortalContextSelection): void => {
    const jwt: any = jwt_decode(token);
    const roles = normalizedRoles(jwt);
    if (expectedContext.Kind === PortalContextKind.Employee) {
        if (jwt[ClaimTypes.PortalContext] !== PortalContextKind.Employee
            || IsNotEmpty(jwt[ClaimTypes.CurrentOrganizationId])
            || roles.length !== 1
            || roles[0] !== Securables.employee_portal)
            throw new Error('The access token does not match the personal employee portal.');
        return;
    }
    if (expectedContext.Kind === PortalContextKind.Producer) {
        const producerContextClaim = getCurrentProducerContextId(jwt);
        if (jwt[ClaimTypes.PortalContext] !== PortalContextKind.Producer
            || producerContextClaim !== expectedContext.ProducerCompanyId
            || IsNotEmpty(jwt[ClaimTypes.CurrentOrganizationId]))
            throw new Error('The producer token does not match the selected producer company.');
        return;
    }

    if (jwt[ClaimTypes.CurrentOrganizationId] !== expectedContext.OrganizationId
        || jwt[ClaimTypes.PortalContext] === PortalContextKind.Employee)
        throw new Error('The organization access token does not match the provisioned organization.');
};

function AreEqual(ai1, ai2) {
    if (ai1.currentUserId !== ai2.currentUserId
        || ai1.selectedOrganizationName !== ai2.selectedOrganizationName
        || ai1.selectedOrganizationId !== ai2.selectedOrganizationId
        || ai1.selectedOrganizationSettings !== ai2.selectedOrganizationSettings
        || ai1.given_name !== ai2.given_name
        || ai1.family_name !== ai2.family_name
        || ai1.email !== ai2.email
        || JSON.stringify(ai1.currentPortalContext) !== JSON.stringify(ai2.currentPortalContext)
        || JSON.stringify(ai1.portalAccess) !== JSON.stringify(ai2.portalAccess)
        || !ArraysEqual(ai1.Roles, ai2.Roles))
        return false;
    return true;
}

function ArraysEqual(arry1, arry2) {
    if (IsNullOrUndefined(arry1) || IsNullOrUndefined(arry2))
        return true;

    if (arry1.length !== arry2.length || intersect(arry1, arry2).length !== arry1.length)
        return false;


}
function intersect(a, b) {
    return a.filter(Set.prototype.has, new Set(b));
}

const accountPolicy = (account): string => {
    const claims = account?.idTokenClaims ?? {};
    return String(claims.tfp ?? claims.acr ?? '').toLowerCase();
};

export const selectAccountForPolicy = (instance, accounts, policyName: string, preferActiveAccount: boolean = false) => {
    const activeAccount = selectActiveAccount(instance, accounts);

    if (preferActiveAccount && activeAccount)
        return activeAccount;

    const expectedPolicy = policyName.toLowerCase();
    return accounts.find(account => accountPolicy(account) === expectedPolicy)
        ?? activeAccount
        ?? accounts[0];
};


export const AccessInfoProviderInner = (props) => {
    const { instance, inProgress, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();

    const [busyContext, setBusyContext] = useState<BusyContext>({ busy: false, msg: '' });
    const [authenticationError, setAuthenticationError] = useState('');
    const postAuthenticatePromise = useRef<Promise<boolean> | null>(null);
    const [needsOnboarding, setNeedsOnboarding] = useState(false);
    const [onboardingBootstrapError, setOnboardingBootstrapError] = useState('');
    const [portalContextTransition, setPortalContextTransition] = useState(false);
    const impersonateInfo = useRef<{ userId?: string, organizationId?: string, withAdmin?: boolean }>();
    const accessTokenInfo = useRef<AccessTokenInfo>({
        token: '',
        tokenExpiration: dayjs()
    })
    const onboardingRefreshTimer = useRef<number | null>(null);
    // Invalidates a refresh that began while the user still had a bootstrap token.
    // clearTimeout cannot stop an already-running async callback from installing its result.
    const onboardingTokenGeneration = useRef(0);
    const employeeInvitationSessionFor = useRef('');
    const employeeInvitationConfirmation = useRef<EmployeeInvitationConfirmation | null>(null);
    const employeeInvitationSessionHandle = useRef('');
    const producerInvitationIdentityReady = useRef(false);
    const [accessInfo, setAccessInfoBacking] = useState<AccessInfoProps>({
        currentUserId: '',
        selectedOrganizationName: '',
        selectedOrganizationId: '',
        selectedProducerCompanyName: '',
        selectedProducerCompanyId: '',
        selectedOrganizationSettings: null,
        roles: Enumerable.from<string>([]),
        organizations: [],
        portalAccess: { Workspaces: [], Producers: [], Employers: [], DefaultEmploymentId: null },
        currentPortalContext: null,
        given_name: '',
        family_name: '',
        email: ''
    });

    const returnPath = sessionStorage.getItem('synergist:b2cReturnPath');
    const preferActiveAccount = IsNotEmpty(returnPath)
        || IsNotEmpty(sessionStorage.getItem(SessionStorageItemIds.InvitationId));
    const account = selectAccountForPolicy(
        instance,
        accounts,
        primarySignInPolicy.name,
        preferActiveAccount);
    const signedInPolicy = String(account?.idTokenClaims?.['tfp'] ?? account?.idTokenClaims?.['acr'] ?? '');
    const signedInAuthority = Object.values(b2cPolicies.authorities)
        .find(entry => entry.authority.toLowerCase().endsWith(`/${signedInPolicy.toLowerCase()}`))?.authority
        ?? signInRequest.authority;

    const accessTokenRequest = {
        authority: signedInAuthority,
        scopes: loginRequest.scopes,
        account,
    };

    const accessTokenRedirectRequest = {
        authority: signInRequest.authority,
        scopes: loginRequest.scopes
    };


    const setAccessInfo = (ai: AccessInfoProps) => {
        if (!AreEqual(ai, accessInfo)) {
            setAccessInfoBacking(ai);
        }
    }

    const setBusy = (msg: string) => {
        setBusyContext({ busy: true, msg });
    }

    const cancelBusy = () => {
        setBusyContext({ busy: false, msg: '' });
    }

    const runWorkspaceBootstrapOperation = async <T,>(operation: () => Promise<T>): Promise<T> => {
        for (let attempt = 0; ; attempt += 1) {
            try {
                return await operation();
            } catch (error) {
                if (!isRetryableWorkspaceBootstrapError(error) || attempt >= workspaceBootstrapRetryDelaysMs.length)
                    throw error;

                setBusy(attempt < 2
                    ? 'Starting your workspace and waking its database…'
                    : 'Your workspace is still starting. This can take about a minute after it has been idle…');

                const requestedDelay = error instanceof ApiRequestError
                    ? error.retryAfterMs
                    : undefined;
                const scheduledDelay = workspaceBootstrapRetryDelaysMs[attempt];
                const delay = requestedDelay === undefined
                    ? scheduledDelay
                    : Math.min(20_000, Math.max(scheduledDelay, requestedDelay));
                await new Promise(resolve => window.setTimeout(resolve, delay));
            }
        }
    };

    const handleLogin = async (redirect: boolean) => {
        const redirectToLogin = async () => {
            if (inProgress !== InteractionStatus.None)
                return;

            try {
                await instance.acquireTokenRedirect(accessTokenRedirectRequest);
            } catch (error) {
                handleRedirectFailure(error);
            }
        };

        const handleRedirectFailure = (error: any) => {
            const errorCode = String(error?.errorCode ?? error?.code ?? '');
            const errorMessage = String(error?.errorMessage ?? error?.message ?? '');
            if (errorCode === 'interaction_in_progress' || errorMessage.includes('interaction_in_progress')) {
                setAuthenticationError('Another secure sign-in is already open. Finish or close that sign-in, then retry here.');
                return;
            }

            setAuthenticationError('We could not open secure sign-in. Check your connection and retry.');
        };

        try {
            console.log("__ Synergist.UI -- Attempting to get a Itoken with refresh token")
            if (IsNotEmpty(account)) {
                let response = await instance.acquireTokenSilent(accessTokenRequest);
                if (IsNotEmpty(response) && IsNotEmpty(response.idToken) && dayjs().add(30, 'seconds').toDate() < response.expiresOn) {
                    console.log("__ Synergist.UI -- Response id token is valid and current")
                    await processPostAuthenticate();
                } else if (redirect) {
                    console.error("__ Synergist.UI -- Response token is not valid, redirecting to the login screen");
                    await redirectToLogin();
                }
            } else if (redirect) {
                console.error("__ Synergist.UI -- No Account locally, redirecting to the login screen");
                await redirectToLogin();
            }
        }
        catch (error) {
            if (redirect) {
                console.error("__ Synergist.UI -- aquireTokenSilent threw an error, redirecting to the login screen")
                console.error(error);
                await redirectToLogin();
            }
        }
    }

    const handleLogout = () => {
        try {
            sessionStorage.removeItem('synergist:b2cReturnPath');
            sessionStorage.removeItem(SessionStorageItemIds.EmployeeInvitationToken);
            sessionStorage.removeItem(SessionStorageItemIds.ProducerInvitationId);
            sessionStorage.removeItem(SessionStorageItemIds.ProducerInvitationToken);
            sessionStorage.removeItem(SessionStorageItemIds.AccessToken);
            accessTokenInfo.current = { token: '', tokenExpiration: dayjs() };
            employeeInvitationSessionFor.current = '';
            employeeInvitationConfirmation.current = null;
            employeeInvitationSessionHandle.current = '';
            producerInvitationIdentityReady.current = false;
            fetchIntercept.clear();
            setAccessInfo({
                ...accessInfo,
                currentUserId: '',
                selectedOrganizationId: '',
                selectedOrganizationName: '',
                selectedOrganizationSettings: null,
                roles: Enumerable.from<string>([]),
                organizations: [],
                portalAccess: { Workspaces: [], Producers: [], Employers: [], DefaultEmploymentId: null },
                currentPortalContext: null,
            });
        }
        catch {
            // Do nothing. We need to call .logout no matter what.
        }
        instance.logoutRedirect();

    }

    const getB2CIdToken = async () => {
        const authResponse = await instance.acquireTokenSilent(accessTokenRequest);
        return authResponse.idToken;
    }

    const scheduleOnboardingTokenRefresh = (expiresAt?: string) => {
        if (onboardingRefreshTimer.current !== null)
            window.clearTimeout(onboardingRefreshTimer.current);
        const expiry = expiresAt ? dayjs(expiresAt) : dayjs().add(19, 'minutes');
        const delay = Math.max(30_000, expiry.subtract(60, 'seconds').diff(dayjs()));
        const generation = onboardingTokenGeneration.current;
        onboardingRefreshTimer.current = window.setTimeout(async () => {
            try {
                const idToken = await getB2CIdToken();
                const refreshed = await new TokenClient().GetOnboardingToken(idToken);
                if (generation !== onboardingTokenGeneration.current)
                    return;
                accessTokenInfo.current.token = refreshed.Token;
                sessionStorage.setItem(SessionStorageItemIds.AccessToken, refreshed.Token);
                registerFetchIntercept(accessTokenInfo.current);
                setOnboardingBootstrapError('');
                scheduleOnboardingTokenRefresh(refreshed.ExpiresAt);
            } catch {
                if (generation !== onboardingTokenGeneration.current)
                    return;
                setOnboardingBootstrapError('Your secure setup session expired. Retry to resume your saved onboarding workspace.');
            }
        }, delay);
    };

    useEffect(() => () => {
        if (onboardingRefreshTimer.current !== null)
            window.clearTimeout(onboardingRefreshTimer.current);
    }, []);
    const updateAccessToken = async (
        ai: AccessInfoProps,
        logoutOnFailure: boolean = true,
        expectedContext?: PortalContextSelection,
        ) => {
        try {
            console.log("__ Synergist.UI -- Get token")

            const idToken = await getB2CIdToken();
            const producerContext = ai.currentPortalContext?.Kind === PortalContextKind.Producer
                ? ai.currentPortalContext
                : null;
            let tokenResponse: TokenResponse;
            if (ai.currentPortalContext?.Kind === PortalContextKind.Employee) {
                tokenResponse = await new TokenClient().GetEmployeePortalToken(idToken);
            } else if (producerContext !== null) {
                tokenResponse = await new TokenClient().GetProducerPortalToken(idToken, producerContext.ProducerCompanyId);
            } else {
                tokenResponse = await new TokenClient().GetAccessToken(
                    ai.selectedOrganizationId,
                    idToken,
                    impersonateInfo.current?.organizationId,
                    impersonateInfo.current?.userId,
                    impersonateInfo.current?.withAdmin);
            }

            // Validate the returned token before it can replace the currently usable
            // token in session storage, the shared ref, or the fetch interceptor.
            if (expectedContext) {
                validatePortalToken(tokenResponse.Token, expectedContext);
            }

            accessTokenInfo.current.token = tokenResponse.Token;
            sessionStorage.setItem(SessionStorageItemIds.AccessToken,  tokenResponse.Token);

            return updateAccessInfoFromAccessToken(ai,  tokenResponse);
        }
        catch (error) {
            console.log(error);
            if (logoutOnFailure)
                handleLogout();
            throw error;
        }
    }

    const updateAccessInfoFromAccessToken = (ai: AccessInfoProps, tokenResponse: TokenResponse) => {
        if (IsNotEmpty(tokenResponse.Token)) {

            let jwt = jwt_decode(tokenResponse.Token);
            // We have to do this because if it comes in with only 1 role, it will make an array out of the characters in the role name
            let roles = normalizedRoles(jwt);
            accessTokenInfo.current = { token: tokenResponse.Token, tokenExpiration: dayjs.unix(jwt[ClaimTypes.TokenExpiration]) };
            registerFetchIntercept(accessTokenInfo.current);

            const tokenOrganizationId = jwt[ClaimTypes.CurrentOrganizationId];
            const tokenProducerCompanyId = getCurrentProducerContextId(jwt);
            let organizationName = ai.organizations.find(x => x.Id === tokenOrganizationId)?.Name;
            const producerFromToken = tokenProducerCompanyId
                ? ai.portalAccess.Producers?.find(x => x.Id === tokenProducerCompanyId)
                : undefined;
            if (IsNotEmpty(impersonateInfo.current?.organizationId)) {
                organizationName = (tokenResponse.Organizations ?? []).find(x => x.Id === impersonateInfo.current.organizationId)?.Name;
                ai.organizations = [...ai.organizations, ...(tokenResponse.Organizations ?? [])];
            }

            var ret = {
                ...ai,
                roles: Enumerable.from<Securables>(roles),
                currentUserId: jwt[ClaimTypes.NameIdentifier],
                    selectedOrganizationId: jwt[ClaimTypes.CurrentOrganizationId] ?? '',
                selectedProducerCompanyId: getCurrentProducerContextId(jwt) ?? '',
                selectedOrganizationName: organizationName ?? '',
                selectedProducerCompanyName: ai.currentPortalContext?.Kind === PortalContextKind.Producer
                    ? producerFromToken?.Name ?? ''
                    : '',
                given_name: jwt[ClaimTypes.given_name],
                family_name: jwt[ClaimTypes.sur_name],
                email: jwt[ClaimTypes.emailaddress],
                impersonatorId: jwt[ClaimTypes.ImpersonatorId],
                ImpersonatorName: jwt[ClaimTypes.ImpersonatorName]
            };

            return ret;
        }
        return { ...ai };
    };

    const loadOrganizations = async (ai: AccessInfoProps): Promise<AccessInfoProps> => {
        console.log("__ Synergist.UI -- Load portal contexts", { Source: 'LoadPortalContexts' })
        const idToken = await getB2CIdToken();
        const portalAccess = await new TokenClient().GetContexts(idToken);
        const producers: ProducerPortalContext[] = (portalAccess.Producers ?? []).map(producer => ({
            Id: producer.Id,
            Name: producer.Name,
            AccessState: producer.AccessState || 'Active',
            Role: producer.Role,
        }));
        const organizations: OrganizationInfo[] = (portalAccess.Workspaces ?? []).map(workspace => ({
            Id: workspace.Id,
            Name: workspace.Name,
            IsSubscriptionActive: workspace.IsSubscriptionActive,
            SubscriptionNotRequired: workspace.SubscriptionNotRequired,
            NeedToValidateSubscription: workspace.NeedToValidateSubscription,
        }));

        let orgs = Enumerable.from<OrganizationInfo>(organizations);
        // linq's first() throws when a valid identity does not belong to an
        // organization yet. Zero organizations is the expected entry point
        // for identity-first onboarding, so read the optional first item from
        // the API response before exchanging the onboarding token.
        const selectedContext = choosePortalContext(
            portalAccess,
            localStorage.getItem(SessionStorageItemIds.LastPortalContext),
            window.location.pathname,
        );
        const selectedWorkspace = selectedContext?.Kind === PortalContextKind.Workspace
            ? organizations.find(organization => organization.Id === selectedContext.OrganizationId)
            : undefined;
        const selectedProducer = selectedContext?.Kind === PortalContextKind.Producer
            ? producers.find(producer => producer.Id === selectedContext.ProducerCompanyId)
            : undefined;

        if (selectedContext)
            localStorage.setItem(SessionStorageItemIds.LastPortalContext, serializePortalContext(selectedContext));
        else
            localStorage.removeItem(SessionStorageItemIds.LastPortalContext);

        if (selectedWorkspace) {
            localStorage.setItem(SessionStorageItemIds.LastOrganizationId, selectedWorkspace.Id);
        } else {
            localStorage.removeItem(SessionStorageItemIds.LastOrganizationId);
        }

        return {
            ...ai,
            organizations: orgs.toArray(),
            portalAccess: { ...portalAccess, Producers: producers },
            currentPortalContext: selectedContext,
            selectedOrganizationId: selectedWorkspace?.Id ?? '',
            selectedOrganizationName: selectedWorkspace?.Name ?? '',
            selectedProducerCompanyId: selectedProducer?.Id ?? '',
            selectedProducerCompanyName: selectedProducer?.Name ?? '',
        };

    }

    const registerFetchIntercept = (ai: AccessTokenInfo) => {
        console.log("__ Synergist.UI -- Register authorized API interceptor");
        fetchIntercept.clear();
        fetchIntercept.register({
            request: async (url, config) => {
                // make sure we are not adding the token to unnecessary requests
                if (!url.includes("Synergist.WebServices") || url.includes('/static/'))
                    return [url, config];

                if (config.headers?.some(x => x[0] === 'Authorization') ?? false)
                    return [url, config]; //Already has authorization, no need to add it

                var header = ['Authorization', `bearer ${await ai.token}`];
                config.headers.push(header);
                console.debug(`__ Synergist.UI -- Added Authorization Header to request: ${url}`);
                return [url, config];
            },

            requestError: (error) => {
                // Called when an error occured during another 'request' interceptor call
                return Promise.reject(error);
            },

            response: (response) => {
                // Modify the reponse object
                return response;
            },

            responseError: (error) => {
                // Handle an fetch error
                return Promise.reject(error);
            }
        });
    }

    const prepareEmployeeInvitationSession = async (
        invitationToken: string,
        signal?: AbortSignal,
        baseAccessInfo: AccessInfoProps = accessInfo,
    ): Promise<EmployeeInvitationConfirmation> => {
        if (employeeInvitationSessionFor.current === invitationToken
            && employeeInvitationConfirmation.current?.State === 'ReadyForConfirmation')
            return employeeInvitationConfirmation.current;

        const idToken = await getB2CIdToken();
        let invitationSession;
        try {
            invitationSession = await runWorkspaceBootstrapOperation(
                () => new EmployeePortalClient().BeginInvitationSession(invitationToken, idToken, signal));
        } finally {
            // This helper also runs for people who were already signed in, outside
            // the normal post-authentication transition and its finally block.
            cancelBusy();
        }
        if (signal?.aborted)
            throw new DOMException('The operation was aborted.', 'AbortError');
        if (invitationSession.Confirmation.State !== 'ReadyForConfirmation')
            throw new Error('The invitation session is not ready for confirmation.');

        // From this point until claim, the browser must operate only through
        // the server's short-lived, identity-bound invitation session handle,
        // retained in memory only. Do not leave a workspace or personal bearer
        // available to unrelated API clients.
        sessionStorage.removeItem(SessionStorageItemIds.AccessToken);
        accessTokenInfo.current = { token: '', tokenExpiration: dayjs() };
        fetchIntercept.clear();
        employeeInvitationSessionFor.current = invitationToken;
        employeeInvitationConfirmation.current = invitationSession.Confirmation;
        employeeInvitationSessionHandle.current = invitationSession.SessionHandle;
        const identityClaims: any = account?.idTokenClaims ?? {};
        const restrictedAccessInfo = {
            ...baseAccessInfo,
            currentUserId: '',
            selectedOrganizationId: '',
            selectedOrganizationName: '',
            selectedOrganizationSettings: null,
            roles: Enumerable.from<string>([]),
            organizations: [],
            portalAccess: { Workspaces: [], Producers: [], Employers: [], DefaultEmploymentId: null },
            currentPortalContext: null,
            given_name: String(identityClaims.given_name ?? ''),
            family_name: String(identityClaims.family_name ?? identityClaims.surname ?? ''),
            email: String(account?.username ?? identityClaims.email ?? identityClaims.emails?.[0] ?? ''),
        };
        setNeedsOnboarding(false);
        setAccessInfo(restrictedAccessInfo);
        return invitationSession.Confirmation;
    };

    const confirmEmployeeInvitation = async (signal?: AbortSignal): Promise<void> => {
        if (!employeeInvitationSessionFor.current
            || employeeInvitationConfirmation.current?.State !== 'ReadyForConfirmation'
            || !employeeInvitationSessionHandle.current)
            throw new Error('The invitation session is not ready for confirmation.');
        const idToken = await getB2CIdToken();
        const sessionHandle = employeeInvitationSessionHandle.current;
        employeeInvitationSessionFor.current = '';
        employeeInvitationConfirmation.current = null;
        employeeInvitationSessionHandle.current = '';
        await new EmployeePortalClient().ClaimInvitation(idToken, sessionHandle, signal);
    };

    useEffect(() => {
        if (isAuthenticated && IsEmpty(accessTokenInfo.current.token)) {
            processPostAuthenticate();
        }
    }, [isAuthenticated]);

    const handleSwitchPortalContext = async (context: PortalContextSelection) => {
        const org = context.Kind === PortalContextKind.Workspace
            ? accessInfo.organizations.find(x => x.Id === context.OrganizationId)
            : undefined;
        const producer = context.Kind === PortalContextKind.Producer
            ? accessInfo.portalAccess.Producers?.find(x => x.Id === context.ProducerCompanyId)
            : undefined;
        if (context.Kind === PortalContextKind.Workspace && IsEmpty(org))
            throw new Error("Invalid organization context");
        if (context.Kind === PortalContextKind.Employee && !hasEmployeePortalAccess(accessInfo.portalAccess))
            throw new Error("The personal employee portal is not available");
        if (context.Kind === PortalContextKind.Producer && !hasProducerPortalAccess(accessInfo.portalAccess))
            throw new Error("The producer portal is not available");
        if (context.Kind === PortalContextKind.Producer && IsEmpty(producer))
            throw new Error("Invalid producer context");

        const previousAccessInfo = accessInfo;
        const previousTokenInfo = accessTokenInfo.current;
        const previousSessionToken = sessionStorage.getItem(SessionStorageItemIds.AccessToken);
        try {
            setPortalContextTransition(true);
            setBusy(context.Kind === PortalContextKind.Employee
                ? "Opening your personal development portal…"
                : context.Kind === PortalContextKind.Producer
                    ? "Opening your producer workspace…"
                    : "Switching organizations…");
            const candidate = {
                ...accessInfo,
                currentPortalContext: context,
                selectedOrganizationId: context.Kind === PortalContextKind.Workspace ? org?.Id : '',
                selectedProducerCompanyId: context.Kind === PortalContextKind.Producer ? producer?.Id : '',
                selectedProducerCompanyName: context.Kind === PortalContextKind.Producer ? producer?.Name : '',
                selectedOrganizationName: org?.Name ?? '',
                selectedOrganizationSettings: null,
            };
            let ai = await updateAccessToken(candidate, false, context);
            if (context.Kind === PortalContextKind.Workspace) {
                const settings = await new OrganizationSettingsClient().GetOnlySettings();
                ai = { ...ai, selectedOrganizationSettings: settings };
            }
            localStorage.setItem(SessionStorageItemIds.LastPortalContext, serializePortalContext(context));
            if (context.Kind === PortalContextKind.Workspace)
                localStorage.setItem(SessionStorageItemIds.LastOrganizationId, ai.selectedOrganizationId);
            else
                localStorage.removeItem(SessionStorageItemIds.LastOrganizationId);
            setAccessInfo(ai);
            return ai;
        } catch (error) {
            accessTokenInfo.current = previousTokenInfo;
            if (previousSessionToken) {
                sessionStorage.setItem(SessionStorageItemIds.AccessToken, previousSessionToken);
                registerFetchIntercept(previousTokenInfo);
            } else {
                sessionStorage.removeItem(SessionStorageItemIds.AccessToken);
                fetchIntercept.clear();
            }
            setAccessInfo(previousAccessInfo);
            throw error;
        } finally {
            setPortalContextTransition(false);
            cancelBusy();
        }
    }

    const handleSwitchOrganization = async (orgId: string) =>
        handleSwitchPortalContext({ Kind: PortalContextKind.Workspace, OrganizationId: orgId });

    const loadOrganizationSettings = async () => {
        const settings = await new OrganizationSettingsClient().GetOnlySettings();
        // localStorage.setItem(SessionStorageItemIds.OrganizationSettings, JSON.stringify(settings));

        setAccessInfo({ ...accessInfo, selectedOrganizationSettings: settings });
    }

    const handleImpersonate = async (userId: string, organizationId: string, withAdmin: boolean) => {
        if (IsEmpty(accessTokenInfo.current.token))
            throw new Error("Must be logged in first");
        if (accessInfo.roles.any(x => x === Securables.synergist_admin) || accessInfo.roles.any(x => x === Securables.impersonate)) {
            impersonateInfo.current = { userId: userId, organizationId: organizationId, withAdmin: withAdmin };
            await processPostAuthenticate();
        } else {
            throw new Error("You do not have access to impersonate");
        }
    }

    const handleClearImpersonate = async () => {
        // remove the organization from the list TODO what if the impersonated user belongs to more than 1 org?
        accessInfo.organizations = accessInfo.organizations.filter(x => x.Id !== impersonateInfo.current.organizationId);
        impersonateInfo.current = { userId: null, organizationId: null, withAdmin: null };
        await processPostAuthenticate();
    }

    const installedTokenMatchesOrganization = (organizationId: string): boolean => {
        const token = sessionStorage.getItem(SessionStorageItemIds.AccessToken);
        if (IsEmpty(token) || accessTokenInfo.current.token !== token)
            return false;
        try {
            const jwt = jwt_decode(token);
            return jwt[ClaimTypes.CurrentOrganizationId] === organizationId;
        } catch {
            return false;
        }
    };

    const installedTokenMatchesContext = (context: PortalContextSelection): boolean => {
        const token = sessionStorage.getItem(SessionStorageItemIds.AccessToken);
        if (IsEmpty(token) || accessTokenInfo.current.token !== token)
            return false;
        try {
            const jwt = jwt_decode(token);
            return context.Kind === PortalContextKind.Employee
                ? jwt[ClaimTypes.PortalContext] === PortalContextKind.Employee
                    : context.Kind === PortalContextKind.Producer
                        ? jwt[ClaimTypes.PortalContext] === PortalContextKind.Producer
                        && getCurrentProducerContextId(jwt) === context.ProducerCompanyId
                    : jwt[ClaimTypes.CurrentOrganizationId] === context.OrganizationId
                        && jwt[ClaimTypes.PortalContext] !== PortalContextKind.Employee;
        } catch {
            return false;
        }
    };

    const processPostAuthenticate = async (preferredOrganizationId?: string): Promise<boolean> => {
        if (postAuthenticatePromise.current) {
            const completed = await postAuthenticatePromise.current;
            return completed && (!preferredOrganizationId || installedTokenMatchesOrganization(preferredOrganizationId));
        }

        const startedInOnboarding = needsOnboarding;
        const authenticate = (async () => {
            // Invalidate any bootstrap-token refresh already awaiting B2C or the API.
            onboardingTokenGeneration.current += 1;
            let discoveredAccess = false;
            let accessDiscoveryCompleted = false;
            try {
                setAuthenticationError('');
                setOnboardingBootstrapError('');
                setBusy("Securely connecting your account and organization…");

                let ai = { ...accessInfo };
                ai = await runWorkspaceBootstrapOperation(() => loadOrganizations(ai));
                accessDiscoveryCompleted = true;
                discoveredAccess = ai.organizations.length > 0
                    || hasEmployeePortalAccess(ai.portalAccess)
                    || hasProducerPortalAccess(ai.portalAccess);

                if (preferredOrganizationId) {
                    const preferred = ai.organizations.find(organization => organization.Id === preferredOrganizationId);
                    if (!preferred)
                        throw new Error('The invited organization is not available to this identity.');
                    ai = {
                        ...ai,
                        currentPortalContext: { Kind: PortalContextKind.Workspace, OrganizationId: preferred.Id },
                        selectedOrganizationId: preferred.Id,
                        selectedOrganizationName: preferred.Name,
                    };
                    localStorage.setItem(SessionStorageItemIds.LastOrganizationId, preferred.Id);
                }

                const employeeInvitationToken = sessionStorage.getItem(SessionStorageItemIds.EmployeeInvitationToken);
                if (IsNotEmpty(employeeInvitationToken)
                    && window.location.pathname.toLowerCase().endsWith('/employeeinvitation')) {
                    await prepareEmployeeInvitationSession(employeeInvitationToken, undefined, ai);
                    sessionStorage.removeItem('synergist:b2cReturnPath');
                    return true;
                }

                const producerInvitationId = sessionStorage.getItem(SessionStorageItemIds.ProducerInvitationId);
                const producerInvitationToken = sessionStorage.getItem(SessionStorageItemIds.ProducerInvitationToken);
                if (IsNotEmpty(producerInvitationId)
                    && IsNotEmpty(producerInvitationToken)
                    && window.location.pathname.toLowerCase().endsWith('/producerportal/invitation')) {
                    // An invitation bootstrap keeps only the verified external identity.
                    // A workspace/Producer bearer must never be sent to the anonymous claim endpoints.
                    sessionStorage.removeItem(SessionStorageItemIds.AccessToken);
                    accessTokenInfo.current = { token: '', tokenExpiration: dayjs() };
                    fetchIntercept.clear();
                    producerInvitationIdentityReady.current = true;
                    const identityClaims: any = account?.idTokenClaims ?? {};
                    setNeedsOnboarding(false);
                    setAccessInfo({
                        ...ai,
                        currentUserId: '',
                        selectedOrganizationId: '',
                        selectedOrganizationName: '',
                        selectedProducerCompanyId: '',
                        selectedProducerCompanyName: '',
                        selectedOrganizationSettings: null,
                        roles: Enumerable.from<string>([]),
                        organizations: [],
                        portalAccess: { Workspaces: [], Producers: [], Employers: [], DefaultEmploymentId: null },
                        currentPortalContext: null,
                        given_name: String(identityClaims.given_name ?? ''),
                        family_name: String(identityClaims.family_name ?? identityClaims.surname ?? ''),
                        email: String(account?.username ?? identityClaims.email ?? identityClaims.emails?.[0] ?? ''),
                    });
                    sessionStorage.removeItem('synergist:b2cReturnPath');
                    return true;
                }

                if (!discoveredAccess
                    && IsEmpty(sessionStorage.getItem(SessionStorageItemIds.InvitationId))
                    && !IDENTITY_FIRST_ONBOARDING) {
                    handleLogout();
                    return false;
                }

                if (!discoveredAccess
                    && IsEmpty(sessionStorage.getItem(SessionStorageItemIds.InvitationId))) {
                    setNeedsOnboarding(true);
                    const idToken = await getB2CIdToken();
                    const onboardingTokenResponse = await runWorkspaceBootstrapOperation(
                        () => new TokenClient().GetOnboardingToken(idToken));
                    accessTokenInfo.current.token = onboardingTokenResponse.Token;
                    sessionStorage.setItem(SessionStorageItemIds.AccessToken, onboardingTokenResponse.Token);
                    ai = updateAccessInfoFromAccessToken(ai, onboardingTokenResponse);
                    scheduleOnboardingTokenRefresh(onboardingTokenResponse.ExpiresAt);
                    setAccessInfo(ai);
                    return true;
                }

                setNeedsOnboarding(false);
                if (onboardingRefreshTimer.current !== null) {
                    window.clearTimeout(onboardingRefreshTimer.current);
                    onboardingRefreshTimer.current = null;
                }
                const expectedContext = ai.currentPortalContext;
                if (!expectedContext)
                    throw new Error('No authorized portal context is available for this account.');
                ai = await runWorkspaceBootstrapOperation(
                    () => updateAccessToken(ai, false, expectedContext));

                // Provisioning is not complete until the installed application token
                // itself proves it is scoped to the organization just created.
                if (preferredOrganizationId &&
                    (ai.selectedOrganizationId !== preferredOrganizationId ||
                     !installedTokenMatchesOrganization(preferredOrganizationId)))
                    throw new Error('The organization access token does not match the provisioned organization.');

                if (expectedContext.Kind === PortalContextKind.Employee) {
                    if (!installedTokenMatchesContext(expectedContext))
                        throw new Error('The personal employee portal token was not installed safely.');
                    setAccessInfo({ ...ai, selectedOrganizationSettings: null });
                    sessionStorage.removeItem('synergist:b2cReturnPath');
                    return true;
                }

                if (expectedContext.Kind === PortalContextKind.Producer) {
                    if (!installedTokenMatchesContext(expectedContext))
                        throw new Error('The producer portal token was not installed safely.');
                    setAccessInfo({ ...ai, selectedOrganizationSettings: null });
                    sessionStorage.removeItem('synergist:b2cReturnPath');
                    return true;
                }

                // This can only be called after we have the new access token ready.
                const settings = await runWorkspaceBootstrapOperation(
                    () => new OrganizationSettingsClient().GetOnlySettings());

                const selectedOrg = ai.organizations.find(x => x.Id === ai.selectedOrganizationId);
                if (!selectedOrg)
                    throw new Error('No organization is available for this account.');

                if (selectedOrg.SubscriptionNotRequired) {
                    selectedOrg.IsSubscriptionActive = true;
                    selectedOrg.NeedToValidateSubscription = false;
                } else if (selectedOrg.NeedToValidateSubscription) {
                    setBusy("Checking your organization access…");
                    const isActive = await runWorkspaceBootstrapOperation(
                        () => new ManageOrganizationClient().CheckSubscriptionIsActive());
                    selectedOrg.IsSubscriptionActive = isActive;
                    selectedOrg.NeedToValidateSubscription = false;
                    ai.organizations = ai.organizations.map(x => x.Id === ai.selectedOrganizationId ? selectedOrg : x);
                }

                setAccessInfo({ ...ai, selectedOrganizationSettings: settings });
                sessionStorage.removeItem('synergist:b2cReturnPath');
                return true;
            } catch (error) {
                console.error("__ Synergist.UI -- Could not finish authenticated workspace setup", error);
                if (isRetryableWorkspaceBootstrapError(error)) {
                    setAuthenticationError('Your secure workspace is still waking up. Retry to keep waiting, or sign out.');
                } else if (IDENTITY_FIRST_ONBOARDING && accessDiscoveryCompleted && !discoveredAccess) {
                    setNeedsOnboarding(true);
                    setOnboardingBootstrapError('We could not start organization setup from this identity. Retry, or sign out and choose another account.');
                } else if (startedInOnboarding) {
                    setOnboardingBootstrapError('Your organization was created, but we could not finish switching to organization access. Retry to continue setup safely.');
                } else {
                    setAuthenticationError('Your account is secure, but Synergist could not load your organization workspace. Check your connection and retry.');
                }
                return false;
            } finally {
                cancelBusy();
            }
        })();

        postAuthenticatePromise.current = authenticate;
        try {
            return await authenticate;
        } finally {
            postAuthenticatePromise.current = null;
        }
    }

    const handleHasRole = (role: Securables, preventOverrides: boolean): boolean => {
        const hasRole = preventOverrides
            ? accessInfo.roles.any(x => x.startsWith(role))
            : accessInfo.roles.any(x => x === Securables.synergist_admin || x === Securables.organization_administrator) || accessInfo.roles.any(x => x.startsWith(role));

        return hasRole;
    }

    const handleHasOneOfRoles = (roles: Securables[], preventOverrides: boolean): boolean => {
        const hasOneOfRoles = preventOverrides
            ? accessInfo.roles.any(air => roles.findIndex(r => air.startsWith(r)) >= 0)
            : accessInfo.roles.any(x => x === Securables.synergist_admin || x === Securables.organization_administrator) || accessInfo.roles.any(air => roles.findIndex(r => air.startsWith(r)) >= 0);

        return hasOneOfRoles;
    }

    const selectedOrganization = accessInfo.organizations.find(
        organization => organization.Id === accessInfo.selectedOrganizationId);
    const employeePortalSelected = accessInfo.currentPortalContext?.Kind === PortalContextKind.Employee;
    const selectedOrganizationHasSubscriptionAccess = employeePortalSelected || hasSubscriptionAccess(selectedOrganization);
    const selectedOrganizationRequiresSubscription = !employeePortalSelected && requiresSubscription(selectedOrganization);
    const authenticationTransitionError = authenticationError || onboardingBootstrapError;
    const invitationIdentityReady = employeeInvitationConfirmation.current?.State === 'ReadyForConfirmation'
        || producerInvitationIdentityReady.current;
    const authenticationTransitionVisible = authenticationTransitionError !== ''
        || busyContext.busy
        || inProgress !== InteractionStatus.None
        || (isAuthenticated
            && IsEmpty(accessTokenInfo.current.token)
            && !invitationIdentityReady);

    return (
        <>
            <AccessInfoContext.Provider value={{
                accessInfo: accessInfo,
                hasSubscriptionAccess: selectedOrganizationHasSubscriptionAccess,
                subscriptionRequired: selectedOrganizationRequiresSubscription,
                hasRole: (role: Securables, preventOverrides: boolean = false) => handleHasRole(role, preventOverrides),
                reloadAccessToken: () => updateAccessToken(accessInfo, true, accessInfo.currentPortalContext ?? undefined),
                hasOneOfRoles: (roles: Securables[], preventOverrides: boolean = false) => handleHasOneOfRoles(roles, preventOverrides),
                changeOrganization: handleSwitchOrganization,
                changePortalContext: handleSwitchPortalContext,
                reloadOrgSettings: loadOrganizationSettings,
                login: () => handleLogin(true),
                startOnboarding: () => {
                    sessionStorage.setItem('synergist:b2cReturnPath', '/Onboarding');
                    instance.loginRedirect({
                        ...onboardingRequest,
                        redirectStartPage: `${window.location.origin}/Onboarding`,
                    });
                },
                startSetupInvitation: (invitationId: string) => {
                    const returnPath = `/SetupInvitation?id=${encodeURIComponent(invitationId)}`;
                    sessionStorage.setItem('synergist:b2cReturnPath', returnPath);
                    instance.loginRedirect({
                        ...onboardingRequest,
                        redirectStartPage: `${window.location.origin}${returnPath}`,
                    });
                },
                startEmployeeInvitation: (invitationToken: string) => {
                    const returnPath = '/EmployeeInvitation';
                    sessionStorage.setItem(SessionStorageItemIds.EmployeeInvitationToken, invitationToken);
                    sessionStorage.setItem('synergist:b2cReturnPath', returnPath);
                    instance.loginRedirect({
                        ...onboardingRequest,
                        prompt: 'select_account',
                        redirectStartPage: `${window.location.origin}${returnPath}`,
                    });
                },
                startProducerInvitation: (invitationId: string, invitationToken: string) => {
                    const returnPath = '/ProducerPortal/Invitation';
                    sessionStorage.setItem(SessionStorageItemIds.ProducerInvitationId, invitationId);
                    sessionStorage.setItem(SessionStorageItemIds.ProducerInvitationToken, invitationToken);
                    sessionStorage.setItem('synergist:b2cReturnPath', returnPath);
                    instance.loginRedirect({
                        ...onboardingRequest,
                        prompt: 'select_account',
                        redirectStartPage: `${window.location.origin}${returnPath}`,
                    });
                },
                getFreshIdentityToken: getB2CIdToken,
                prepareEmployeeInvitationSession: (invitationToken: string, signal?: AbortSignal) =>
                    prepareEmployeeInvitationSession(invitationToken, signal),
                confirmEmployeeInvitation: (signal?: AbortSignal) => confirmEmployeeInvitation(signal),
                switchEmployeeInvitationIdentity: (invitationToken: string) => {
                    const returnPath = '/EmployeeInvitation';
                    sessionStorage.removeItem(SessionStorageItemIds.AccessToken);
                    accessTokenInfo.current = { token: '', tokenExpiration: dayjs() };
                    employeeInvitationSessionFor.current = '';
                    employeeInvitationConfirmation.current = null;
                    employeeInvitationSessionHandle.current = '';
                    fetchIntercept.clear();
                    setAccessInfo({
                        ...accessInfo,
                        currentUserId: '',
                        selectedOrganizationId: '',
                        selectedOrganizationName: '',
                        selectedOrganizationSettings: null,
                        roles: Enumerable.from<string>([]),
                        organizations: [],
                        portalAccess: { Workspaces: [], Producers: [], Employers: [], DefaultEmploymentId: null },
                        currentPortalContext: null,
                    });
                    sessionStorage.setItem(SessionStorageItemIds.EmployeeInvitationToken, invitationToken);
                    sessionStorage.setItem('synergist:b2cReturnPath', returnPath);
                    instance.setActiveAccount?.(null);
                    instance.logoutRedirect({ account, postLogoutRedirectUri: `${window.location.origin}${returnPath}` });
                },
                logout: handleLogout,
                impersonate: handleImpersonate,
                clearImpersonation: handleClearImpersonate,
                isImpersonating: () => accessInfo.impersonatorId !== null && accessInfo.impersonatorId !== undefined,
                needsOnboarding,
                onboardingBootstrapError,
                reloadAfterOnboardingProvisioned: (organizationId: string) => installedTokenMatchesOrganization(organizationId)
                    ? Promise.resolve(true)
                    : processPostAuthenticate(organizationId),
                reloadAfterInvitationAccepted: (organizationId: string) => installedTokenMatchesOrganization(organizationId)
                    ? Promise.resolve(true)
                    : processPostAuthenticate(organizationId),
                reloadAfterEmployeeInvitationClaimed: () => processPostAuthenticate(),
                reloadAfterProducerInvitationClaimed: (organizationId?: string) => {
                    sessionStorage.removeItem(SessionStorageItemIds.ProducerInvitationId);
                    sessionStorage.removeItem(SessionStorageItemIds.ProducerInvitationToken);
                    producerInvitationIdentityReady.current = false;
                    return processPostAuthenticate(organizationId);
                },
                isLoggedIn: () => {
                    const accessToken = sessionStorage.getItem(SessionStorageItemIds.AccessToken);
                    return isAuthenticated
                        && ((IsNotEmpty(accessToken) && accessTokenInfo.current.token === accessToken)
                            || (window.location.pathname.toLowerCase().endsWith('/employeeinvitation')
                                && employeeInvitationConfirmation.current?.State === 'ReadyForConfirmation')
                            || (window.location.pathname.toLowerCase().endsWith('/producerportal/invitation')
                                && producerInvitationIdentityReady.current));
                },
            }}>
                <AuthenticationTransitionBoundary
                    visible={authenticationTransitionVisible}
                    unmountContent={portalContextTransition || (isAuthenticated
                        && IsEmpty(accessTokenInfo.current.token)
                        && !invitationIdentityReady)}
                    error={authenticationTransitionError || undefined}
                    message={busyContext.msg || 'Completing secure sign-in…'}
                    onRetry={() => {
                        // A cross-tab redirect can still own the MSAL interaction
                        // after this tab has rendered its recovery message. Keep
                        // that actionable message in place until a retry can
                        // actually start instead of replacing it with a spinner.
                        if (inProgress !== InteractionStatus.None)
                            return;

                        setAuthenticationError('');
                        if (!isAuthenticated && IsEmpty(account))
                            void handleLogin(true);
                        else
                            void processPostAuthenticate();
                    }}
                    onSignOut={handleLogout}
                >
                    {props.children}
                </AuthenticationTransitionBoundary>
            </AccessInfoContext.Provider>
        </>
    );

}

const AccessInfoProvider = (props) => {
    return (
        <MsalProvider instance={msalInstance}>
            <AccessInfoProviderInner {...props} />
        </MsalProvider>
    );
}

export const useAccessInfo = () => useContext(AccessInfoContext);

export default AccessInfoProvider;
